import React, { createContext, useContext, useMemo, useRef } from 'react';
import type {
  ProfileSettings,
  UserMetadataV2,
  UserPermissionV2,
  GetUserPreferencesV2CommandOutput,
} from '@amzn/gbsa-orbit-typescript-client';
import {
  useUserDetailsQuery,
  useUserMetadataQuery,
  useUserPermissionsQuery,
} from 'src/data/api/orbit-service-ts/user/queries';
import { useUserPreferencesQuery } from 'src/data/api/orbit-service-ts/user-preferences/queries';
import type { Permission } from 'src/contstant/permissionAccessConfig';
import { APP_PROGRAM_ID } from './constants';
import { useMidway } from './useMidway';
import { useServiceSessions } from './useServiceSessions';
import { usePreferredLocationValue } from '../data/preferred-location';

export type UserPreferences = GetUserPreferencesV2CommandOutput['preferences'] & {
  profileSettings: ProfileSettings & {
    businessUnitLocationPreferences: string[];
  };
};

export interface CurrentUserContextValue {
  /** Authenticated user alias (Midway sub claim). */
  readonly alias: string;
  /** Configured program identifier for this app. */
  readonly programId: string;
  /**
   * Backend user record. Undefined for a first-time user who hasn't
   * picked a business unit yet — the user-details query is BU-scoped
   * and stays idle without a BU. The profile preferences page is the
   * one consumer that handles this state.
   */
  readonly user: UserPermissionV2 | undefined;
  /** Backend user metadata (full name, etc.). */
  readonly userMetadata: UserMetadataV2;
  /** User preferences. May be missing a business unit on first login. */
  readonly userPreferences: UserPreferences;
  /** Set of permissions granted to the user. Empty until BU is set. */
  readonly permissions: ReadonlySet<Permission>;
  /**
   * Active business unit, if the user has selected one. The profile
   * preferences page handles the first-time case where this is undefined.
   */
  readonly businessUnit: string | undefined;
  /**
   * User's preferred location code for the active BU, read from
   * localStorage. Used as the default value for location filters across
   * the app. Undefined when no preferred location is set or no BU is
   * active.
   */
  readonly preferredLocation: string | undefined;
  /**
   * Location codes the user has saved for the active BU (from Orbit
   * preferences). Used to scope location filter options. Empty array
   * when no BU is set or no locations are saved.
   */
  readonly savedLocations: readonly string[];
}

const CurrentUserContext = createContext<CurrentUserContextValue | null>(null);

/**
 * Exported for the DesktopAuthBridge — allows the hybrid V2Adapter to provide
 * the same context that useCurrentUser() reads from. Normal V2 code should
 * NOT use this directly; use useCurrentUser() instead.
 */
export { CurrentUserContext };

export interface UserProviderProps {
  /** Rendered while the auth chain is in flight. */
  readonly loadingFallback: React.ReactNode;
  /** Rendered when the auth chain fails. Receives the underlying error. */
  readonly errorFallback: (error: Error) => React.ReactNode;
  readonly children: React.ReactNode;
}

const NO_PERMISSIONS: ReadonlySet<Permission> = new Set();

/**
 * v2 UserProvider.
 *
 * Owns the auth chain for v2:
 *   1. Midway hands us an alias.
 *   2. Service sessions are warmed up — each backing service (Helios,
 *      Orbit) Set-Cookies its session cookie. Without this, the first
 *      cross-origin API call on a fresh browser session is rejected
 *      with a CORS error because there's no cookie to send.
 *   3. Orbit query hooks fetch user, metadata, preferences.
 *   4. Permissions query runs once a business unit is set.
 *
 * Loading and error states render the supplied fallbacks. The provider does
 * not gate on missing business unit — the profile preferences page is the
 * one place that handles the "first-time user" workflow, and it needs the
 * context too.
 */
export function UserProvider({
  loadingFallback,
  errorFallback,
  children,
}: UserProviderProps): React.ReactElement {
  const { userAlias: alias, isError: isMidwayError, error: midwayError } = useMidway();

  // Stage 2: warm up service-session cookies. Gated on alias internally —
  // stays in loading state until Midway hands us one.
  const {
    isReady: areSessionsReady,
    isError: isSessionError,
    error: sessionError,
  } = useServiceSessions(alias);

  const programId = APP_PROGRAM_ID;

  // Every Orbit hook below is gated on `enabled: areSessionsReady` so we
  // don't fire a credentialed request before the service has Set-Cookied.
  const { data: preferences, error: preferencesError } = useUserPreferencesQuery(
    { userId: alias, programId },
    { enabled: areSessionsReady },
  );

  const businessUnit = preferences?.profileSettings?.businessUnit;

  const { data: metadata, error: metadataError } = useUserMetadataQuery(
    { userId: alias, programId },
    { enabled: areSessionsReady, select: (data) => data.userMetadata },
  );

  const { data: user, error: userError } = useUserDetailsQuery(
    { userId: alias, programId, businessUnit },
    {
      enabled: areSessionsReady && !!businessUnit,
      select: (data) => data.userDetails.userPermission,
    },
  );

  const divisions = user?.divisions ?? [];

  // Permissions query is gated on businessUnit by the underlying hook, so
  // it stays idle until the user has completed first-time setup.
  const { data: permissions, error: permissionsError } = useUserPermissionsQuery(
    { userId: alias, programId, businessUnit, divisions },
    { enabled: areSessionsReady },
  );

  const firstError =
    midwayError ??
    sessionError ??
    preferencesError ??
    metadataError ??
    userError ??
    permissionsError;

  // Reactive localStorage read — re-renders when the profile preferences
  // page writes a new preferred location, so Audits/Tasks pick it up
  // without a refresh.
  const preferredLocation = usePreferredLocationValue(alias, businessUnit);

  const value = useMemo<CurrentUserContextValue | null>(() => {
    // `user` is intentionally allowed to be undefined: the user-details
    // query is gated on `!!businessUnit`, and a first-time user has no
    // BU. Gating the context value on `user` would lock those users out
    // of the profile preferences page — exactly the page they need to
    // pick a BU.
    if (!alias || !preferences || !metadata) return null;

    const savedLocations: readonly string[] =
      preferences.profileSettings?.businessUnitPreferences?.find(
        (entry) => entry.businessUnit === businessUnit,
      )?.locations ?? [];

    return {
      alias,
      programId,
      user,
      userMetadata: metadata,
      userPreferences: preferences,
      permissions: permissions ?? NO_PERMISSIONS,
      businessUnit,
      preferredLocation,
      savedLocations,
    };
  }, [
    alias,
    preferences,
    metadata,
    user,
    permissions,
    businessUnit,
    programId,
    preferredLocation,
  ]);

  // Keep the last successfully-built context value around so downstream
  // refetches (e.g. switching business unit invalidates user-preferences,
  // which cascades into useUserDetailsQuery and briefly produces undefined
  // data while the new key resolves) don't tear the whole subtree down
  // and back up. Re-mounting children would also re-fire `useBundle` in
  // arb-tools, which momentarily resolves message keys to their raw key
  // names — visible to the user as flashing untranslated labels.
  const lastGoodValueRef = useRef<CurrentUserContextValue | null>(null);
  if (value !== null) {
    lastGoodValueRef.current = value;
  }
  const renderValue = value ?? lastGoodValueRef.current;

  if (isMidwayError && midwayError) return <>{errorFallback(midwayError)}</>;
  if (isSessionError && sessionError) return <>{errorFallback(sessionError)}</>;
  if (firstError) return <>{errorFallback(firstError)}</>;
  if (renderValue === null) return <>{loadingFallback}</>;

  return (
    <CurrentUserContext.Provider value={renderValue}>
      {children}
    </CurrentUserContext.Provider>
  );
}

/**
 * Read the current user. Throws if called outside <UserProvider>.
 */
export function useCurrentUser(): CurrentUserContextValue {
  const value = useContext(CurrentUserContext);
  if (value === null) {
    throw new Error('useCurrentUser must be used within <UserProvider>');
  }
  return value;
}
